import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, TrendingUp, TrendingDown, Minus, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sentiment color mapping
const SENTIMENT_COLORS = {
  positive: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', icon: TrendingUp },
  negative: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', icon: TrendingDown },
  neutral: { bg: 'bg-white/10', text: 'text-white/60', border: 'border-white/20', icon: Minus }
};

const NewsTicker = ({ className = '' }) => {
  const [newsItems, setNewsItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  
  // Fetch news from RSS API
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news/rss/ticker?limit=20`, {
          timeout: 10000
        });
        setNewsItems(response.data.ticker || []);
      } catch (error) {
        console.error('Error fetching news:', error);
        // Fallback to simulated news if API fails
        setNewsItems(getSimulatedNews());
      }
      setIsLoading(false);
    };
    
    fetchNews();
    
    // Refresh every 15 minutes (matches cache duration)
    const interval = setInterval(fetchNews, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Fallback simulated news
  const getSimulatedNews = () => [
    { id: '1', character: 'Elon Musk', title: 'SpaceX announces new Starship launch', sentiment: { label: 'positive' }, link: '#', source: 'TechCrunch' },
    { id: '2', character: 'Donald Trump', title: 'Political rally draws thousands', sentiment: { label: 'neutral' }, link: '#', source: 'CNN' },
    { id: '3', character: 'Bitcoin', title: 'Crypto markets surge past $100k', sentiment: { label: 'positive' }, link: '#', source: 'CoinDesk' },
  ];
  
  // Handle news item click
  const handleNewsClick = (item) => {
    if (item.link && item.link !== '#') {
      window.open(item.link, '_blank', 'noopener,noreferrer');
    }
  };
  
  if (isLoading || newsItems.length === 0) {
    return (
      <div className={`bg-black/60 border-y border-white/10 py-2 ${className}`}>
        <div className="flex items-center justify-center">
          <Zap className="w-4 h-4 text-yellow-400 animate-pulse mr-2" />
          <span className="font-space text-sm text-white/40">Loading live news...</span>
        </div>
      </div>
    );
  }
  
  // Duplicate items for seamless scrolling
  const duplicatedItems = [...newsItems, ...newsItems];
  
  return (
    <div 
      className={`relative overflow-hidden bg-gradient-to-r from-black/80 via-black/60 to-black/80 border-y border-white/10 ${className}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Breaking News Badge */}
      <div className="absolute left-0 top-0 bottom-0 z-20 flex items-center bg-gradient-to-r from-black via-black to-transparent pr-8">
        <div className="flex items-center gap-2 bg-red-600 px-3 py-1 rounded-r-full">
          <Zap className="w-4 h-4 text-white animate-pulse" />
          <span className="font-anton text-xs text-white uppercase tracking-wider">Breaking</span>
        </div>
      </div>
      
      {/* Scrolling Ticker */}
      <motion.div 
        className="flex whitespace-nowrap py-2.5 pl-32"
        animate={{
          x: isPaused ? 0 : [0, `-${50}%`]
        }}
        transition={{
          x: {
            duration: newsItems.length * 8,
            repeat: Infinity,
            ease: 'linear',
            repeatType: 'loop'
          }
        }}
      >
        {duplicatedItems.map((item, index) => {
          const sentiment = SENTIMENT_COLORS[item.sentiment?.label] || SENTIMENT_COLORS.neutral;
          const SentimentIcon = sentiment.icon;
          
          return (
            <button
              key={`${item.id}-${index}`}
              onClick={() => handleNewsClick(item)}
              className={`inline-flex items-center gap-3 mx-4 px-4 py-1.5 rounded-full ${sentiment.bg} ${sentiment.border} border transition-all duration-200 hover:scale-105 cursor-pointer group`}
              data-testid={`news-item-${item.id}`}
            >
              <SentimentIcon className={`w-4 h-4 ${sentiment.text}`} />
              <span className="font-space text-sm text-white/80">
                <span className={`font-bold ${sentiment.text}`}>{item.character}</span>
                {' '}
                {item.title}
              </span>
              {item.source && (
                <span className="font-space text-xs text-white/40">
                  — {item.source}
                </span>
              )}
              <ExternalLink className="w-3 h-3 text-white/30 group-hover:text-white/60 transition-colors" />
              
              {/* Power modifier badge */}
              {item.sentiment?.power_modifier !== 0 && (
                <span className={`font-anton text-xs px-1.5 py-0.5 rounded ${
                  item.sentiment.power_modifier > 0 ? 'bg-green-500/30 text-green-400' : 'bg-red-500/30 text-red-400'
                }`}>
                  {item.sentiment.power_modifier > 0 ? '+' : ''}{item.sentiment.power_modifier}★
                </span>
              )}
            </button>
          );
        })}
      </motion.div>
      
      {/* Right fade effect */}
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />
    </div>
  );
};

export default NewsTicker;
